/* eslint-disable */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import XLSX from "xlsx-js-style";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  InputAdornment,
  Avatar,
  Autocomplete,
} from "@mui/material";

import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  AttachFile as AttachFileIcon,
  ChatBubbleOutlined as ChatBubbleIcon,
} from "@mui/icons-material";

import { FaFileExcel, FaTasks, FaProjectDiagram } from "react-icons/fa";

import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import { selectProjects } from "store/features/company/projectManagement/project/projectSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { getUtilizationReport } from "services/company/projectManagement/report/reportService";

// Expandable Task Row with Tabular Remarks & Reviews (Clean Rectangular Styling)
function TaskRow({ task, index, isDark }) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const remarksList = task.all_reviews_remarks || [];
  const hasMultipleRemarks = remarksList.length > 1;

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          "& > *": { borderBottom: open ? "unset" : undefined },
          transition: "background-color 0.15s ease",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {/* 1. Sl No */}
        <TableCell
          align="center"
          sx={{
            width: 55,
            fontWeight: 600,
            color: "text.secondary",
            fontSize: "0.82rem",
          }}
        >
          {task.sl_no || index + 1}
        </TableCell>

        {/* 2. Date */}
        <TableCell sx={{ minWidth: 110, fontSize: "0.82rem", fontWeight: 500 }}>
          {task.date || "—"}
        </TableCell>

        {/* 3. Project Name */}
        <TableCell sx={{ minWidth: 190 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.84rem" }}>
              {task.project_name || "General"}
            </Typography>
            {task.project_code && (
              <Chip
                label={task.project_code}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  borderRadius: "4px",
                  color: "primary.main",
                  borderColor: "divider",
                }}
              />
            )}
          </Stack>
          {task.title && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mt: 0.3, fontSize: "0.75rem" }}
              noWrap
            >
              Task: {task.title}
            </Typography>
          )}
        </TableCell>

        {/* 4. Assigned By */}
        <TableCell sx={{ minWidth: 150 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "0.72rem",
                bgcolor: "primary.light",
                color: "primary.contrastText",
                fontWeight: 600,
                borderRadius: "4px",
              }}
            >
              {(task.assigned_by || "A").charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.82rem" }}>
              {task.assigned_by || "Admin"}
            </Typography>
          </Stack>
        </TableCell>

        {/* 5. Expected Time */}
        <TableCell sx={{ minWidth: 110 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "0.82rem" }}>
            {task.expected_time || "0h 00m"}
          </Typography>
        </TableCell>

        {/* 6. Actual Time */}
        <TableCell sx={{ minWidth: 110 }}>
          <Chip
            label={task.actual_time || "0h 00m"}
            size="small"
            color="primary"
            variant="filled"
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              borderRadius: "4px",
              height: 22,
            }}
          />
        </TableCell>

        {/* 7. Remarks */}
        <TableCell sx={{ minWidth: 260 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography
              variant="body2"
              sx={{
                color: task.latest_remark && task.latest_remark !== "—" ? "text.primary" : "text.secondary",
                fontStyle: !task.latest_remark || task.latest_remark === "—" ? "italic" : "normal",
                fontSize: "0.82rem",
                maxWidth: 320,
              }}
              noWrap
            >
              {task.latest_remark && task.latest_remark !== "—" ? task.latest_remark : "No remarks"}
            </Typography>

            {remarksList.length > 0 && (
              <Tooltip title={open ? "Hide full remarks" : `View all ${remarksList.length} remarks/reviews`}>
                <IconButton
                  size="small"
                  onClick={() => setOpen(!open)}
                  sx={{
                    p: 0.5,
                    borderRadius: "4px",
                    bgcolor: open ? "action.selected" : "transparent",
                    color: open ? "primary.main" : "text.secondary",
                  }}
                >
                  {hasMultipleRemarks && (
                    <Chip
                      label={`${remarksList.length}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, borderRadius: "4px", mr: 0.5 }}
                    />
                  )}
                  {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expandable Row for Full Remarks & Reviews Trail (Clean Rectangular) */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                p: 1.5,
                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f9fafb",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <ChatBubbleIcon sx={{ fontSize: "0.95rem", color: "primary.main" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                  Remarks & Reviews for: "{task.title || task.project_name}" ({remarksList.length})
                </Typography>
              </Stack>

              {remarksList.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", fontSize: "0.8rem" }}>
                  No remarks or reviews recorded for this task.
                </Typography>
              ) : (
                <Table size="small" sx={{ bgcolor: "background.paper", borderRadius: "4px", border: `1px solid ${theme.palette.divider}` }}>
                  <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.76rem" }}>Author / Reviewer</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.76rem" }}>Category / Purpose</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.76rem" }}>Remark / Review Note</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.76rem" }}>Deliverable / File</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.76rem" }}>Submitted At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remarksList.map((item, idx) => (
                      <TableRow key={item.id || idx} hover>
                        <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>
                          {item.author || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.category || (item.type === "review" ? "Review" : "Remark")}
                            size="small"
                            variant="outlined"
                            color={item.type === "review" ? "info" : "default"}
                            sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, borderRadius: "4px" }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 380, whiteSpace: "pre-wrap", fontSize: "0.78rem" }}>
                          {item.note || item.remark || item.review || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.78rem" }}>
                          {item.deliverables ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AttachFileIcon sx={{ fontSize: "0.8rem", color: "primary.main" }} />
                              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 500, fontSize: "0.75rem" }}>
                                {String(item.deliverables).split("/").pop()}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          {item.created_at ? dayjs(item.created_at).format("DD MMM YYYY, hh:mm A") : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function UtilizationReport() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();

  // Redux state
  const user = useSelector((state) => state.auth?.user || {});
  const userRole = (user?.role || "").toLowerCase();
  const isCompanyOrAdmin = userRole === "company" || userRole === "super_admin" || userRole === "admin";

  const allProjectsFromStore = useSelector(selectProjects);
  const allEmployeesFromStore = useSelector(
    (state) => state.companyEmployee?.employees || []
  );

  // Filters State
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("all");
  const [periodPreset, setPeriodPreset] = useState("this_month");
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [searchQuery, setSearchQuery] = useState("");

  // Report Data & Loading State
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Initial data dispatch
  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Handle Preset Changes
  const handlePresetChange = (preset) => {
    setPeriodPreset(preset);
    const now = dayjs();
    if (preset === "this_month") {
      setStartDate(now.startOf("month"));
      setEndDate(now.endOf("month"));
    } else if (preset === "last_month") {
      const last = now.subtract(1, "month");
      setStartDate(last.startOf("month"));
      setEndDate(last.endOf("month"));
    } else if (preset === "last_30_days") {
      setStartDate(now.subtract(29, "day"));
      setEndDate(now);
    }
  };

  // Fetch Report Data
  const loadReport = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const payload = {
        filters: {
          employee_id: selectedEmployeeId ? Number(selectedEmployeeId) : undefined,
          project_id: selectedProjectId && selectedProjectId !== "all" ? Number(selectedProjectId) : undefined,
          from_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
          to_date: endDate ? endDate.format("YYYY-MM-DD") : undefined,
        },
      };

      const res = await getUtilizationReport(payload);
      // services/api.js returns response.data directly!
      const reportPayload = res?.data || res;
      const isSuccess =
        res?.meta?.status === true ||
        res?.data?.meta?.status === true ||
        Boolean(reportPayload && (reportPayload.selected_employee || reportPayload.stats || reportPayload.tasks));

      if (isSuccess && reportPayload) {
        setReportData(reportPayload);
        setErrorMsg(null);
      } else {
        const msg =
          res?.meta?.message ||
          res?.data?.meta?.message ||
          res?.message ||
          "Failed to load Utilization Report";
        setErrorMsg(msg);
      }
    } catch (err) {
      console.error("Utilization report fetch error:", err);
      setErrorMsg(
        err.response?.data?.meta?.message ||
        err.meta?.message ||
        err.message ||
        "Failed to load report"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedEmployeeId, selectedProjectId, startDate, endDate]);

  // Load report when filters change
  useEffect(() => {
    loadReport();
  }, [selectedEmployeeId, selectedProjectId, startDate, endDate]);

  // Available employees for single-select dropdown (scoping by hierarchy from backend response or store)
  const availableEmployees = useMemo(() => {
    if (reportData?.available_employees && reportData.available_employees.length > 0) {
      return reportData.available_employees;
    }
    return allEmployeesFromStore.map((e) => ({
      id: e.id,
      employee_code: e.employee_code || `EMP-${e.id}`,
      name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.employee_code,
      email: e.email,
      department_name: e.department?.name || "General",
      designation_name: e.designation?.name || "Employee",
      avatar: e.profile_pic_url,
      is_self: Boolean(
        (user?.id && (e.id === user.id || e.user_id === user.id)) ||
        (user?.email && e.email && e.email.toLowerCase() === user.email.toLowerCase())
      ),
    }));
  }, [reportData?.available_employees, allEmployeesFromStore, user]);

  const selectedEmployeeObj = useMemo(() => {
    if (!selectedEmployeeId) return reportData?.selected_employee || availableEmployees[0] || null;
    return availableEmployees.find((e) => String(e.id) === String(selectedEmployeeId)) || reportData?.selected_employee || null;
  }, [selectedEmployeeId, availableEmployees, reportData?.selected_employee]);

  // Hierarchy-based available projects strictly from reportData
  const availableProjects = useMemo(() => {
    if (reportData?.available_projects && Array.isArray(reportData.available_projects) && reportData.available_projects.length > 0) {
      return reportData.available_projects;
    }
    if (reportData?.projects && Array.isArray(reportData.projects) && reportData.projects.length > 0) {
      return reportData.projects.map((rp) => ({
        id: rp.project_id,
        name: rp.project_name,
        code: rp.project_code,
      }));
    }
    return [];
  }, [reportData?.available_projects, reportData?.projects]);

  // Auto-reset selectedProjectId if not in hierarchy-scoped projects
  useEffect(() => {
    if (selectedProjectId !== "all" && availableProjects.length > 0) {
      const exists = availableProjects.some((p) => String(p.id) === String(selectedProjectId));
      if (!exists) {
        setSelectedProjectId("all");
      }
    }
  }, [availableProjects, selectedProjectId]);

  // Stats & Projects from report response
  const stats = reportData?.stats || {
    total_actual_hours: 0,
    total_actual_formatted: "0h 00m",
    total_expected_hours: 0,
    total_expected_formatted: "0h 00m",
    total_projects: 0,
    total_tasks: 0,
    completed_tasks: 0,
    in_progress_tasks: 0,
    capacity_hours: 0,
    utilization_rate: 0,
  };

  const projectCards = reportData?.projects || [];
  const rawTasks = reportData?.tasks || [];

  // Filter tasks by search query
  const filteredTasks = useMemo(() => {
    if (!searchQuery.trim()) return rawTasks;
    const q = searchQuery.toLowerCase();
    return rawTasks.filter((t) => {
      const title = (t.title || "").toLowerCase();
      const proj = (t.project_name || "").toLowerCase();
      const code = (t.project_code || "").toLowerCase();
      const assigned = (t.assigned_by || "").toLowerCase();
      const remark = (t.latest_remark || "").toLowerCase();
      return (
        title.includes(q) ||
        proj.includes(q) ||
        code.includes(q) ||
        assigned.includes(q) ||
        remark.includes(q)
      );
    });
  }, [rawTasks, searchQuery]);

  // Client-Side Tabular Excel Export matching UI
  const handleExportExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      const emp = selectedEmployeeObj || reportData?.selected_employee || {};
      const period = reportData?.period || {};

      // Common styling templates for xlsx-js-style
      const thinBorder = {
        top: { style: "thin", color: { rgb: "D1D5DB" } },
        bottom: { style: "thin", color: { rgb: "D1D5DB" } },
        left: { style: "thin", color: { rgb: "D1D5DB" } },
        right: { style: "thin", color: { rgb: "D1D5DB" } },
      };

      const cell = (v, custom = {}) => ({
        v: v ?? "",
        s: {
          font: { name: "Calibri", sz: 10, color: { rgb: "1F2937" }, ...(custom.font || {}) },
          fill: custom.fill,
          alignment: { vertical: "center", horizontal: "left", ...(custom.alignment || {}) },
          border: custom.border !== undefined ? custom.border : thinBorder,
        },
      });

      const sectionTitleStyle = {
        font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "334155" } }, // Slate 700
        alignment: { horizontal: "left", vertical: "center" },
      };

      const tableHeadStyle = {
        font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2563EB" } }, // Enterprise Blue
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
      };

      const labelStyle = {
        font: { bold: true, sz: 10, color: { rgb: "374151" } },
        fill: { fgColor: { rgb: "F3F4F6" } },
        alignment: { horizontal: "left", vertical: "center" },
      };

      const valStyle = {
        font: { sz: 10, color: { rgb: "111827" } },
        alignment: { horizontal: "left", vertical: "center" },
      };

      const dataCenterStyle = {
        alignment: { horizontal: "center", vertical: "center" },
      };

      const dataBoldStyle = {
        font: { bold: true, sz: 10, color: { rgb: "1E40AF" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const summaryRows = [];
      const merges = [];

      // Row 0: Main Title Banner
      const titleRowIdx = summaryRows.length;
      summaryRows.push([
        cell("EMPLOYEE UTILIZATION REPORT", {
          font: { sz: 14, bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "1E3A8A" } }, // Navy 900
          alignment: { horizontal: "center", vertical: "center" },
        }),
      ]);
      merges.push({ s: { r: titleRowIdx, c: 0 }, e: { r: titleRowIdx, c: 7 } });
      summaryRows.push([]); // blank separator

      // Employee Information Block (Structured 4-Column Tabular Grid)
      summaryRows.push([
        cell("Employee Name", labelStyle),
        cell(emp.name || "—", valStyle),
        cell("Department", labelStyle),
        cell(emp.department_name || "General", valStyle),
        cell("Period", labelStyle),
        cell(
          `${period.from_date || startDate.format("YYYY-MM-DD")} to ${period.to_date || endDate.format("YYYY-MM-DD")}`,
          valStyle
        ),
      ]);

      summaryRows.push([
        cell("Employee Code", labelStyle),
        cell(emp.employee_code || "—", valStyle),
        cell("Designation", labelStyle),
        cell(emp.designation_name || "Employee", valStyle),
        cell("Working Days", labelStyle),
        cell(`${period.working_days || 0} Days (${stats.capacity_hours || 0} hrs capacity)`, valStyle),
      ]);

      summaryRows.push([
        cell("Report Month", labelStyle),
        cell(period.month_label || startDate.format("MMMM YYYY"), valStyle),
        cell("Generated On", labelStyle),
        cell(dayjs().format("DD MMM YYYY, hh:mm A"), valStyle),
        cell("Overall Utilization", labelStyle),
        cell(`${stats.utilization_rate || 0}%`, {
          ...valStyle,
          font: { bold: true, sz: 10, color: { rgb: stats.utilization_rate >= 75 ? "15803D" : "1D4ED8" } },
        }),
      ]);

      summaryRows.push([]); // blank separator

      // 1. MONTHLY PERFORMANCE SUMMARY TABLE
      const sec1Idx = summaryRows.length;
      summaryRows.push([cell("1. MONTHLY PERFORMANCE SUMMARY", sectionTitleStyle)]);
      merges.push({ s: { r: sec1Idx, c: 0 }, e: { r: sec1Idx, c: 7 } });

      summaryRows.push([
        cell("Hours Worked This Month", tableHeadStyle),
        cell("Expected Task Time", tableHeadStyle),
        cell("Monthly Capacity", tableHeadStyle),
        cell("Projects Worked On", tableHeadStyle),
        cell("Total Tasks", tableHeadStyle),
        cell("Tasks Done", tableHeadStyle),
        cell("In Progress", tableHeadStyle),
        cell("Utilization Rate", tableHeadStyle),
      ]);

      summaryRows.push([
        cell(stats.total_actual_formatted || "0h 00m", dataBoldStyle),
        cell(stats.total_expected_formatted || "0h 00m", dataCenterStyle),
        cell(`${stats.capacity_hours || 0} hrs (${period.working_days || 0} Days)`, dataCenterStyle),
        cell(`${stats.total_projects || 0} Projects`, dataCenterStyle),
        cell(stats.total_tasks || 0, dataCenterStyle),
        cell(stats.completed_tasks || 0, {
          font: { bold: true, color: { rgb: "15803D" } },
          alignment: { horizontal: "center", vertical: "center" },
        }),
        cell(stats.in_progress_tasks || 0, {
          font: { bold: true, color: { rgb: "D97706" } },
          alignment: { horizontal: "center", vertical: "center" },
        }),
        cell(`${stats.utilization_rate || 0}%`, dataBoldStyle),
      ]);

      summaryRows.push([]); // blank separator

      // 2. PROJECTS WORKED ON THIS MONTH TABLE
      const sec2Idx = summaryRows.length;
      summaryRows.push([cell(`2. PROJECTS WORKED ON THIS MONTH (${projectCards.length})`, sectionTitleStyle)]);
      merges.push({ s: { r: sec2Idx, c: 0 }, e: { r: sec2Idx, c: 7 } });

      summaryRows.push([
        cell("Sl No", tableHeadStyle),
        cell("Project Name", tableHeadStyle),
        cell("Project Code", tableHeadStyle),
        cell("Worked Time", tableHeadStyle),
        cell("Expected Time", tableHeadStyle),
        cell("Tasks Completed", tableHeadStyle),
        cell("Total Tasks", tableHeadStyle),
        cell("Progress (%)", tableHeadStyle),
      ]);

      if (projectCards.length === 0) {
        summaryRows.push([
          cell("—", dataCenterStyle),
          cell("No project activities recorded for this period", { font: { italic: true } }),
          cell("—", dataCenterStyle),
          cell("0h 00m", dataCenterStyle),
          cell("0h 00m", dataCenterStyle),
          cell(0, dataCenterStyle),
          cell(0, dataCenterStyle),
          cell("0%", dataCenterStyle),
        ]);
      } else {
        projectCards.forEach((p, idx) => {
          summaryRows.push([
            cell(idx + 1, dataCenterStyle),
            cell(p.project_name || "—"),
            cell(p.project_code || "—", dataCenterStyle),
            cell(p.worked_formatted || "0h 00m", dataBoldStyle),
            cell(p.expected_formatted || "0h 00m", dataCenterStyle),
            cell(p.completed_count || 0, dataCenterStyle),
            cell(p.task_count || 0, dataCenterStyle),
            cell(`${p.progress_percent || 0}%`, dataCenterStyle),
          ]);
        });
      }

      summaryRows.push([]); // blank separator

      // 3. TASKS REPORT TABLE (Exact 7 Columns Matching UI)
      const sec3Idx = summaryRows.length;
      summaryRows.push([cell(`3. TASKS REPORT (${rawTasks.length} Tasks)`, sectionTitleStyle)]);
      merges.push({ s: { r: sec3Idx, c: 0 }, e: { r: sec3Idx, c: 6 } });

      summaryRows.push([
        cell("Sl No", tableHeadStyle),
        cell("Date", tableHeadStyle),
        cell("Project Name", tableHeadStyle),
        cell("Assigned By", tableHeadStyle),
        cell("Expected Time", tableHeadStyle),
        cell("Actual Time", tableHeadStyle),
        cell("Remarks", tableHeadStyle),
      ]);

      if (rawTasks.length === 0) {
        summaryRows.push([
          cell("—", dataCenterStyle),
          cell("—", dataCenterStyle),
          cell("No tasks found for this employee and period", { font: { italic: true } }),
          cell("—", dataCenterStyle),
          cell("0h 00m", dataCenterStyle),
          cell("0h 00m", dataCenterStyle),
          cell("—", dataCenterStyle),
        ]);
      } else {
        rawTasks.forEach((t, idx) => {
          const projectDisplay = t.project_code
            ? `${t.project_name || "General"} (${t.project_code})`
            : t.project_name || "General";
          const fullProjectAndTask = t.title ? `${projectDisplay} - ${t.title}` : projectDisplay;

          summaryRows.push([
            cell(t.sl_no || idx + 1, dataCenterStyle),
            cell(t.date || "—", dataCenterStyle),
            cell(fullProjectAndTask),
            cell(t.assigned_by || "Admin"),
            cell(t.expected_time || "0h 00m", dataCenterStyle),
            cell(t.actual_time || "0h 00m", dataBoldStyle),
            cell(t.latest_remark || t.remarks || "—"),
          ]);
        });
      }

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
      wsSummary["!merges"] = merges;
      wsSummary["!cols"] = [
        { wch: 8 },  // Sl No
        { wch: 15 }, // Date
        { wch: 36 }, // Project Name
        { wch: 22 }, // Assigned By
        { wch: 16 }, // Expected Time
        { wch: 16 }, // Actual Time
        { wch: 50 }, // Remarks
        { wch: 18 }, // Extra summary column
      ];
      XLSX.utils.book_append_sheet(workbook, wsSummary, "Utilization Summary");

      // Sheet 2: Dedicated Tasks Details Table
      const taskDetailRows = [];
      taskDetailRows.push([
        cell("Sl No", tableHeadStyle),
        cell("Date", tableHeadStyle),
        cell("Project Name", tableHeadStyle),
        cell("Project Code", tableHeadStyle),
        cell("Task Title", tableHeadStyle),
        cell("Assigned By", tableHeadStyle),
        cell("Expected Time", tableHeadStyle),
        cell("Actual Time", tableHeadStyle),
        cell("Status", tableHeadStyle),
        cell("Remarks", tableHeadStyle),
      ]);

      rawTasks.forEach((t, idx) => {
        taskDetailRows.push([
          cell(t.sl_no || idx + 1, dataCenterStyle),
          cell(t.date || "—", dataCenterStyle),
          cell(t.project_name || "General"),
          cell(t.project_code || "—", dataCenterStyle),
          cell(t.title || "—"),
          cell(t.assigned_by || "Admin"),
          cell(t.expected_time || "0h 00m", dataCenterStyle),
          cell(t.actual_time || "0h 00m", dataBoldStyle),
          cell(t.status || "—", dataCenterStyle),
          cell(t.latest_remark || t.remarks || "—"),
        ]);
      });

      const wsTasks = XLSX.utils.aoa_to_sheet(taskDetailRows);
      wsTasks["!cols"] = [
        { wch: 8 },
        { wch: 14 },
        { wch: 26 },
        { wch: 16 },
        { wch: 32 },
        { wch: 20 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 50 },
      ];
      XLSX.utils.book_append_sheet(workbook, wsTasks, "Tasks Details");

      const cleanEmpName = (emp.name || "employee").replace(/[^a-zA-Z0-9_-]/g, "_");
      const filename = `utilization_report_${cleanEmpName}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } catch (e) {
      console.error("Excel export failed:", e);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, maxWidth: 1600, mx: "auto" }}>
      {/* 1. TOP HEADER & FILTERS BAR (Clean Rectangular Enterprise Paper, exactly like Todays Status Report) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: "4px",
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          {/* Header Title & Employee Info */}
          <Grid item xs={12} lg={5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
                Utilization Report
              </Typography>
              <Chip
                label={`${stats.utilization_rate || 0}% UTILIZED`}
                size="small"
                color={
                  stats.utilization_rate >= 100
                    ? "warning"
                    : stats.utilization_rate >= 75
                    ? "success"
                    : stats.utilization_rate > 0
                    ? "info"
                    : "default"
                }
                sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22, borderRadius: "4px" }}
              />
              <Chip
                label={reportData?.period?.month_label || startDate.format("MMMM YYYY")}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: "4px" }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Employee: <strong>{selectedEmployeeObj?.name || "Employee"}</strong> ({selectedEmployeeObj?.employee_code || "EMP"}) •{" "}
              {selectedEmployeeObj?.designation_name || "Employee"} • {selectedEmployeeObj?.department_name || "General"}
            </Typography>
          </Grid>

          {/* Filters & Export Action (Wider, Clean Rectangular Controls) */}
          <Grid item xs={12} lg={7}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              justifyContent={{ xs: "flex-start", lg: "flex-end" }}
              flexWrap="wrap"
            >
              {/* Employee Selector (Generous width, clean 4px border) */}
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 240 } }}>
                <InputLabel id="emp-select-label">Employee</InputLabel>
                <Select
                  labelId="emp-select-label"
                  value={selectedEmployeeId || ""}
                  label="Employee"
                  onChange={(e) => {
                    setSelectedEmployeeId(e.target.value);
                    setSelectedProjectId("all");
                  }}
                  sx={{ borderRadius: "4px" }}
                >
                  <MenuItem value="">
                    <em>{isCompanyOrAdmin ? "Select Employee" : "My Report (Default)"}</em>
                  </MenuItem>
                  {availableEmployees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.employee_code}){emp.is_self ? " - Me" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Period / Month Selector (Clean Rectangular) */}
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                <InputLabel id="period-preset-label">Period</InputLabel>
                <Select
                  labelId="period-preset-label"
                  value={periodPreset}
                  label="Period"
                  onChange={(e) => handlePresetChange(e.target.value)}
                  sx={{ borderRadius: "4px" }}
                >
                  <MenuItem value="this_month">This Month ({dayjs().format("MMM")})</MenuItem>
                  <MenuItem value="last_month">Last Month ({dayjs().subtract(1, "month").format("MMM")})</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>

              {/* Hierarchy-Scoped Project Filter (Clean Rectangular, Generous Width) */}
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id="project-filter-label">Project</InputLabel>
                <Select
                  labelId="project-filter-label"
                  value={selectedProjectId}
                  label="Project"
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  sx={{ borderRadius: "4px" }}
                >
                  <MenuItem value="all">
                    All Projects {availableProjects.length > 0 ? `(${availableProjects.length})` : ""}
                  </MenuItem>
                  {availableProjects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} {p.code ? `(${p.code})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Excel Export Button */}
              <Button
                variant="contained"
                color="success"
                startIcon={<FaFileExcel style={{ fontSize: "1rem" }} />}
                onClick={handleExportExcel}
                disabled={loading || rawTasks.length === 0}
                sx={{
                  borderRadius: "4px",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 2,
                  height: 38,
                }}
              >
                Export Excel
              </Button>

              {/* Refresh Button */}
              <IconButton
                onClick={loadReport}
                disabled={loading}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  p: 0.8,
                }}
                title="Refresh"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>

        {/* Custom Date Pickers (Shown if "custom" is selected) */}
        {periodPreset === "custom" && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(newVal) => setStartDate(newVal)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "4px" } },
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(newVal) => setEndDate(newVal)}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "4px" } },
                    },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>
        )}
      </Paper>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Error Message */}
      {!loading && errorMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            bgcolor: "error.lighter",
            color: "error.main",
            borderRadius: "4px",
            border: `1px solid ${theme.palette.error.light}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {errorMsg}
          </Typography>
        </Paper>
      )}

      {!loading && !errorMsg && (
        <>
          {/* 2. OVERALL MONTHLY SUMMARY TABLE (Clean Rectangular Enterprise Table, exactly like Todays Status Report) */}
          <Box sx={{ mb: 2.5 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "4px",
              }}
            >
              <Table size="small" aria-label="monthly utilization summary table">
                <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Hours Worked This Month</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Expected Task Time</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Monthly Capacity</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Projects Worked On</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Total Tasks</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Tasks Done</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem" }}>In Progress</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.78rem" }}>Utilization Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: "0.95rem" }}>
                      {stats.total_actual_formatted || "0h 00m"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {stats.total_expected_formatted || "0h 00m"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.82rem" }}>
                      {stats.capacity_hours || 0} hrs ({reportData?.period?.working_days || 0} Working Days)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {stats.total_projects || 0} Projects
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                      {stats.total_tasks || 0}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "success.main", fontSize: "0.85rem" }}>
                      {stats.completed_tasks || 0}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "primary.main", fontSize: "0.85rem" }}>
                      {stats.in_progress_tasks || 0}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      {stats.utilization_rate || 0}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* 3. PROJECTS WORKED ON (Clean Rectangular Enterprise Cards with borderRadius: 4px, separate from report) */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: "4px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FaProjectDiagram style={{ color: theme.palette.primary.main, fontSize: "0.95rem" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  Projects Worked On This Month ({projectCards.length})
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Breakdown of logged hours vs expected time per project
              </Typography>
            </Stack>

            {projectCards.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: "4px",
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f9fafb",
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", fontSize: "0.82rem" }}>
                  No project activities or logged hours recorded for this employee in the selected period.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {projectCards.map((p) => (
                  <Grid item xs={12} sm={6} md={4} key={p.project_id}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "4px",
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fdfdfd",
                        transition: "border-color 0.15s ease",
                        "&:hover": { borderColor: theme.palette.primary.main },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.86rem" }} noWrap>
                          {p.project_name}
                        </Typography>
                        {p.project_code && (
                          <Chip
                            label={p.project_code}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 19,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              borderRadius: "4px",
                              color: "primary.main",
                              borderColor: "divider",
                            }}
                          />
                        )}
                      </Stack>

                      {/* Worked Hours vs Estimated */}
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                          Worked Time:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.main", fontSize: "0.82rem" }}>
                          {p.worked_formatted || "0h 00m"}{" "}
                          <Typography component="span" variant="caption" sx={{ color: "text.secondary", fontSize: "0.74rem" }}>
                            / {p.expected_formatted || "0h 00m"} est
                          </Typography>
                        </Typography>
                      </Stack>

                      {/* Tasks Progress */}
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 0.5, mb: 0.8 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                          Tasks Completed:
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                          {p.completed_count} of {p.task_count} ({p.progress_percent}%)
                        </Typography>
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={Math.min(p.progress_percent, 100)}
                        sx={{
                          height: 5,
                          borderRadius: "2px",
                          bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: "2px",
                            bgcolor: p.progress_percent >= 100 ? "success.main" : "primary.main",
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

          {/* 4. TASKS REPORT TABLE (Exact 7 Columns, Clean Rectangular Table like Todays Status Report) */}
          <Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FaTasks style={{ fontSize: "0.95rem" }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                  Tasks Report ({filteredTasks.length})
                </Typography>
              </Stack>

              {/* Quick Search inside Tasks Table */}
              <TextField
                size="small"
                placeholder="Search tasks, project, remarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 260 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    bgcolor: "background.paper",
                    fontSize: "0.82rem",
                  },
                }}
              />
            </Stack>

            {filteredTasks.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  textAlign: "center",
                  borderRadius: "4px",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No tasks recorded for the selected employee and period.
                </Typography>
              </Paper>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                }}
              >
                <Table aria-label="tasks utilization table" size="small">
                  <TableHead sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.78rem", width: 55 }}>
                        Sl No
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 110 }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 190 }}>
                        Project Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 150 }}>
                        Assigned By
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 110 }}>
                        Expected Time
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 110 }}>
                        Actual Time
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", minWidth: 260 }}>
                        Remarks
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredTasks.map((task, idx) => (
                      <TaskRow key={task.id || idx} task={task} index={idx} isDark={isDark} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filteredTasks.length > 0 && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1.5, px: 0.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredTasks.length} tasks • Expand any task row with remarks to view full history
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  Total Logged: {stats.total_actual_formatted || "0h 00m"}
                </Typography>
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
